import * as THREE from 'three';

/**
 * Shared fog-of-war uniforms. The fog texture stores per-tile visibility
 * (0 unexplored, ~0.5 explored, 1 visible) and is sampled with linear filtering
 * so edges are soft.
 */
export const fowUniforms = {
  uFowTex: { value: null as THREE.Texture | null },
  uFowSize: { value: 128 },
  uFowEnabled: { value: 1 },
  uTime: { value: 0 },
};

/**
 * Patch a built-in material so its output is darkened by fog of war.
 * Works with instanced and non-instanced meshes.
 */
export function applyFow(mat: THREE.Material, extra?: (shader: THREE.WebGLProgramParametersWithUniforms) => void): void {
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = (shader, renderer) => {
    prev?.call(mat, shader, renderer);
    shader.uniforms.uFowTex = fowUniforms.uFowTex;
    shader.uniforms.uFowSize = fowUniforms.uFowSize;
    shader.uniforms.uFowEnabled = fowUniforms.uFowEnabled;
    shader.uniforms.uTime = fowUniforms.uTime;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vFowWorld;\nuniform float uTime;')
      .replace(
        '#include <project_vertex>',
        `#include <project_vertex>
        {
          vec4 fw = vec4(transformed, 1.0);
          #ifdef USE_INSTANCING
            fw = instanceMatrix * fw;
          #endif
          vFowWorld = (modelMatrix * fw).xyz;
        }`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vFowWorld;\nuniform sampler2D uFowTex;\nuniform float uFowSize;\nuniform float uFowEnabled;')
      .replace(
        '#include <dithering_fragment>',
        `#include <dithering_fragment>
        if (uFowEnabled > 0.5) {
          float fv = texture2D(uFowTex, vFowWorld.xz / uFowSize).r;
          float lit = smoothstep(0.0, 0.5, fv) * 0.45 + smoothstep(0.5, 1.0, fv) * 0.55;
          gl_FragColor.rgb *= lit;
        }`,
      );
    extra?.(shader);
  };
  mat.customProgramCacheKey = () => 'fow' + (extra ? extra.toString().length : 0);
}

/** Inline GLSL snippet for custom ShaderMaterials. */
export const FOW_GLSL = `
uniform sampler2D uFowTex;
uniform float uFowSize;
uniform float uFowEnabled;
float fowLight(vec3 wp) {
  if (uFowEnabled < 0.5) return 1.0;
  float fv = texture2D(uFowTex, wp.xz / uFowSize).r;
  return smoothstep(0.0, 0.5, fv) * 0.45 + smoothstep(0.5, 1.0, fv) * 0.55;
}`;
